import { useState, useEffect } from 'react';
import { Link, useSearchParams, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, ShieldCheck, ArrowLeft, XCircle, Save, Send } from 'lucide-react';
import PublicFaq from '../../components/PublicFaq';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Public apply + pay (£1,500)
export const AssuranceProgramme = () => {
  const [sp] = useSearchParams();
  const [fee, setFee] = useState({ fee: 1500, currency: 'GBP' });
  const [f, setF] = useState({
    company_name: sp.get('company') || '', contact_name: '', email: '',
    product_name: '', website: '', notes: '', slug: sp.get('slug') || '',
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => { axios.get(`${API}/public/assurance/meta`).then((r) => setFee(r.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.company_name.trim() || !f.email.trim()) { toast.error('Company and email are required'); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/assurance/apply`, {
        ...f, company_name: f.company_name.trim(), email: f.email.trim(),
        slug: f.slug || null, origin_url: window.location.origin,
      });
      if (data.url) window.location.href = data.url;
      else { toast.error('Could not start checkout'); setBusy(false); }
    } catch { toast.error('Something went wrong. Please try again.'); setBusy(false); }
  };

  const field = 'w-full px-4 py-3 border border-[#E5E7EB] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#00A79D]/40';
  return (
    <div data-testid="page-assurance">
      <section className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <Link to="/browse" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#00A79D] mb-6" data-testid="assurance-back"><ArrowLeft className="w-4 h-4" /> Back to the directory</Link>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#00A79D] bg-[#00A79D]/10 px-3 py-1 rounded-full mb-5"><ShieldCheck className="w-3.5 h-3.5" /> Care Tech Assurance</span>
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tight font-extrabold text-gray-900 leading-tight">Earn the independent assurance mark.</h1>
            <p className="text-lg text-gray-600 mt-5">A structured, independent review of your care-technology product. Pass, and your directory listing carries the Care Tech Assurance mark, evidence buyers can trust.</p>
            <ul className="mt-6 space-y-2">
              {['A structured independent review of your product', 'The verified assurance mark on your directory listing', 'Evidence care providers can rely on when choosing'].map((x) => (
                <li key={x} className="flex items-center gap-2 text-gray-700"><CheckCircle2 className="w-4 h-4 text-[#00A79D]" /> {x}</li>
              ))}
            </ul>
            <div className="mt-8 inline-flex items-baseline gap-2 bg-[#F9F9F9] border border-[#E5E7EB] rounded-lg px-5 py-4">
              <span className="text-3xl font-extrabold text-gray-900">£{Number(fee.fee).toLocaleString()}</span>
              <span className="text-sm text-gray-500">one-off assurance fee</span>
            </div>
          </div>

          <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 lg:sticky lg:top-24">
            <h2 className="font-heading text-xl font-semibold text-gray-900 mb-1">Apply for assurance</h2>
            <p className="text-sm text-gray-500 mb-4">Complete the form and pay securely to begin your review.</p>
            <form onSubmit={submit} className="space-y-3" data-testid="assurance-form">
              <input type="text" placeholder="Company name *" value={f.company_name} onChange={(e) => setF({ ...f, company_name: e.target.value })} className={field} data-testid="assurance-company" />
              <input type="text" placeholder="Product name" value={f.product_name} onChange={(e) => setF({ ...f, product_name: e.target.value })} className={field} data-testid="assurance-product" />
              <input type="text" placeholder="Your name" value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} className={field} data-testid="assurance-contact" />
              <input type="url" placeholder="Website" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} className={field} data-testid="assurance-website" />
              <input type="email" required placeholder="Work email *" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={field} data-testid="assurance-email" />
              <textarea rows={2} placeholder="Anything we should know? (optional)" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className={field} data-testid="assurance-notes" />
              <button type="submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold px-6 py-3.5 rounded-md transition-colors disabled:opacity-60" data-testid="assurance-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Apply & pay £{Number(fee.fee).toLocaleString()}
              </button>
              <p className="text-[11px] text-gray-400 text-center">Secure payment via Stripe. You'll be redirected to complete checkout.</p>
            </form>
          </div>
        </div>
      </section>

      <PublicFaq id="assurance" heading="Care City Assurance FAQs"
        intro="What independent assurance means for care technology and how the review works."
        faqs={[
          { q: 'What is the Care City Assurance programme?', a: 'It is an independent review of a care technology product, carried out in partnership with Care City. It examines areas that matter to care buyers - such as data security, interoperability, usability and evidence of outcomes - so providers can trust that a product has been scrutinised by a neutral third party rather than just its own marketing.' },
          { q: 'Why does assurance matter to care providers?', a: 'Choosing digital systems carries real risk around data protection, integration and reliability. An assurance review gives care providers, and the people they answer to, confidence that a product meets a recognised standard - reducing procurement risk and speeding up decisions.' },
          { q: 'How does a supplier get assured?', a: 'Suppliers apply through Care Tech Compare, submit evidence about their product, and take part in a structured review. Once complete, an assurance badge is displayed on their listing so care buyers can see the product has been independently examined.' },
          { q: 'Is assurance the same as a review from care providers?', a: 'No - they complement each other. Peer reviews capture real-world experience from care providers using the product day to day, while Care City assurance is a structured, expert evaluation against defined criteria. Together they give a fuller picture of a product.' },
        ]} />
    </div>
  );
};

// Return page - poll payment status
export const AssuranceSuccess = () => {
  const [sp] = useSearchParams();
  const sessionId = sp.get('session_id');
  const [state, setState] = useState('checking'); // checking | paid | pending | error

  useEffect(() => {
    if (!sessionId) { setState('error'); return; }
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 6) { setState('pending'); return; }
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/public/assurance/status/${sessionId}`);
        if (data.payment_status === 'paid') { setState('paid'); return; }
        if (data.status === 'expired') { setState('error'); return; }
        setTimeout(poll, 2000);
      } catch { setState('error'); }
    };
    poll();
  }, [sessionId]);

  return (
    <div data-testid="page-assurance-success">
      <section className="bg-[#F9F9F9]">
        <div className="max-w-xl mx-auto px-4 py-24 text-center">
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-10">
            {state === 'checking' && <><Loader2 className="w-10 h-10 text-[#00A79D] mx-auto mb-4 animate-spin" /><p className="font-semibold text-lg text-gray-900" data-testid="assurance-status-checking">Confirming your payment…</p></>}
            {state === 'paid' && <><CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-4" /><h1 className="font-heading text-3xl font-extrabold text-gray-900" data-testid="assurance-status-paid">Payment received, thank you.</h1><p className="text-gray-600 mt-3">Your application is now in our assurance review queue. We'll be in touch with next steps.</p><Link to="/browse" className="inline-flex items-center gap-1.5 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold px-6 py-3 rounded-md mt-6 transition-colors">Back to the directory</Link></>}
            {state === 'pending' && <><Loader2 className="w-10 h-10 text-amber-500 mx-auto mb-4" /><p className="font-semibold text-lg text-gray-900" data-testid="assurance-status-pending">Payment is processing.</p><p className="text-gray-600 mt-2">This can take a moment, we'll email your confirmation shortly.</p></>}
            {state === 'error' && <><XCircle className="w-10 h-10 text-red-500 mx-auto mb-4" /><p className="font-semibold text-lg text-gray-900" data-testid="assurance-status-error">We couldn't confirm this payment.</p><Link to="/assurance" className="inline-flex items-center gap-1.5 text-[#00A79D] font-semibold mt-4">Try again</Link></>}
          </div>
        </div>
      </section>
    </div>
  );
};


// Supplier evidence submission (secure token link, no auth)
export const AssuranceEvidence = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [resp, setResp] = useState({});
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/public/assurance/evidence/${token}`)
      .then((r) => { setData(r.data); setResp(r.data.responses || {}); setSubmitted(r.data.submitted); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [token]);

  const setField = (key, field, value) => setResp((p) => ({ ...p, [key]: { ...(p[key] || {}), [field]: value } }));

  const persist = async (submit) => {
    setBusy(true);
    try {
      const { data: r } = await axios.post(`${API}/public/assurance/evidence/${token}`, { responses: resp, submit });
      if (submit) { setSubmitted(true); toast.success('Evidence submitted for review'); }
      else toast.success('Draft saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Something went wrong'); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-32"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  if (notFound || !data) return (
    <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="evidence-not-found">
      <XCircle className="w-10 h-10 text-red-500 mx-auto mb-4" />
      <p className="font-semibold text-gray-900">This evidence link is not valid.</p>
      <p className="text-sm text-gray-600 mt-2">It may have expired or the application hasn't been paid yet.</p>
    </div>
  );

  const locked = data.locked || submitted;
  const field = 'w-full px-4 py-3 border border-[#E5E7EB] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#00A79D]/40 disabled:bg-gray-50 disabled:text-gray-500';

  return (
    <div data-testid="page-assurance-evidence">
      <section className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#00A79D] bg-[#00A79D]/10 px-3 py-1 rounded-full mb-5"><ShieldCheck className="w-3.5 h-3.5" /> Care Tech Assurance</span>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-extrabold text-gray-900 leading-tight">Submit your assurance evidence</h1>
          <p className="text-lg text-gray-600 mt-4">For <span className="font-semibold text-gray-900">{data.company_name}</span>{data.product_name ? ` - ${data.product_name}` : ''}. Answer each criterion below. You can save a draft and return via this link at any time.</p>

          {submitted && (
            <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center gap-3" data-testid="evidence-submitted-banner">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              <p className="text-sm text-emerald-800 font-medium">Your evidence has been submitted. Our assessor will review it and we'll be in touch with the outcome.</p>
            </div>
          )}

          <div className="mt-8 space-y-6">
            {data.criteria.map((c, i) => (
              <div key={c.key} className="border border-[#E5E7EB] rounded-lg p-5" data-testid={`evidence-criterion-${c.key}`}>
                <p className="font-heading text-lg font-semibold text-gray-900">{i + 1}. {c.label}</p>
                <p className="text-sm text-gray-500 mt-1 mb-3">{c.prompt}</p>
                <textarea rows={4} disabled={locked} placeholder="Your response" value={(resp[c.key] || {}).answer || ''}
                  onChange={(e) => setField(c.key, 'answer', e.target.value)} className={field} data-testid={`evidence-answer-${c.key}`} />
                <input type="url" disabled={locked} placeholder="Supporting link (optional)" value={(resp[c.key] || {}).link || ''}
                  onChange={(e) => setField(c.key, 'link', e.target.value)} className={`${field} mt-2`} data-testid={`evidence-link-${c.key}`} />
              </div>
            ))}
          </div>

          {!locked && (
            <div className="flex flex-wrap gap-3 mt-8">
              <button onClick={() => persist(false)} disabled={busy} className="inline-flex items-center gap-2 border border-[#E5E7EB] text-gray-700 font-semibold px-6 py-3 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-60" data-testid="evidence-save-draft">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save draft
              </button>
              <button onClick={() => persist(true)} disabled={busy} className="inline-flex items-center gap-2 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold px-6 py-3 rounded-md transition-colors disabled:opacity-60" data-testid="evidence-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Submit for review
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
